/**
 * Runtime boundary for untrusted track documents (imports, storage, pasted
 * JSON). Nothing is read from the input until its type is checked. Unknown
 * keys are errors so a typo can't be silently ignored, and every count is
 * bounded before anything is allocated from it. The result is a fresh,
 * normalised document, never the input object itself.
 */
import type { ReactiveBehavior, SurfaceVisual } from '../world/SurfaceMaterials';
import type { Vec3Tuple } from '../world/LevelData';
import { BUILTIN_MATERIALS } from '../world/SurfaceMaterials';
import {
  ENVIRONMENT_PRESETS,
  LIMITS,
  OBJECT_TYPES,
  RULESETS,
  TRACK_FORMAT,
  TRACK_FORMAT_VERSION,
  type CustomMaterial,
  type EditorMeta,
  type EnvironmentPreset,
  type ObjectType,
  type ParamSpec,
  type ParamValue,
  type SketchNote,
  type SketchStroke,
  type TrackDocument,
  type TrackObject,
  type TrackPaint,
  type TrackSketch,
} from './TrackDocument';
import { PAINT_HEIGHT_CODES, PAINT_KIND_CODES, decodeRuns } from './paintCodec';

export type ParseResult = { ok: true; doc: TrackDocument } | { ok: false; errors: string[] };

const ID_RE = /^[A-Za-z0-9_-]+$/;
const MAX_ERRORS = 50;

class Errors {
  readonly list: string[] = [];
  add(path: string, msg: string): void {
    if (this.list.length < MAX_ERRORS) this.list.push(`${path}: ${msg}`);
  }
  get full(): boolean {
    return this.list.length >= MAX_ERRORS;
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function keys(v: Record<string, unknown>, allowed: readonly string[], path: string, e: Errors): void {
  for (const k of Object.keys(v)) if (!allowed.includes(k)) e.add(path, `unknown field "${k}"`);
}

function str(v: unknown, path: string, e: Errors, max: number, required = true): string {
  if (typeof v !== 'string') {
    if (required || v !== undefined) e.add(path, 'must be a string');
    return '';
  }
  if (v.length > max) e.add(path, `longer than ${max} characters`);
  return v.slice(0, max);
}

function num(v: unknown, path: string, e: Errors, min: number, max: number): number {
  if (!isNum(v)) {
    e.add(path, 'must be a finite number');
    return min;
  }
  if (v < min || v > max) e.add(path, `must be between ${min} and ${max}`);
  return v;
}

function vec3(v: unknown, path: string, e: Errors, bound: number): Vec3Tuple {
  if (!Array.isArray(v) || v.length !== 3 || !v.every(isNum)) {
    e.add(path, 'must be [x, y, z] finite numbers');
    return [0, 0, 0];
  }
  const t = v as number[];
  if (t.some((n) => Math.abs(n) > bound)) e.add(path, `coordinates must be within ±${bound}`);
  return [t[0]!, t[1]!, t[2]!];
}

function id(v: unknown, path: string, e: Errors): string {
  const s = str(v, path, e, LIMITS.maxIdLength);
  if (s && !ID_RE.test(s)) e.add(path, 'may only contain letters, digits, "_" and "-"');
  if (!s) e.add(path, 'must not be empty');
  return s;
}

function paramValue(spec: ParamSpec, v: unknown, path: string, e: Errors): ParamValue {
  switch (spec.kind) {
    case 'number':
      return num(v, path, e, spec.min, spec.max);
    case 'int': {
      const n = num(v, path, e, spec.min, spec.max);
      if (!Number.isInteger(n)) e.add(path, 'must be an integer');
      return n;
    }
    case 'bool':
      if (typeof v !== 'boolean') e.add(path, 'must be true or false');
      return v === true;
    case 'enum':
      if (typeof v !== 'string' || !spec.options.includes(v)) {
        e.add(path, `must be one of ${spec.options.join(', ')}`);
        return spec.default;
      }
      return v;
    case 'text':
      return str(v, path, e, spec.maxLength);
    case 'color':
      if (!Number.isInteger(v) || (v as number) < 0 || (v as number) > 0xffffff) e.add(path, 'must be an integer colour 0x000000–0xffffff');
      return isNum(v) ? v : spec.default;
    case 'points': {
      if (!Array.isArray(v)) {
        e.add(path, 'must be a list of [x, y, z] points');
        return spec.default;
      }
      if (v.length < spec.minCount || v.length > spec.maxCount) e.add(path, `needs ${spec.minCount}–${spec.maxCount} points`);
      return v.slice(0, spec.maxCount).map((pt, i) => vec3(pt, `${path}[${i}]`, e, LIMITS.maxCoord));
    }
  }
}

function object(v: unknown, path: string, e: Errors): TrackObject | null {
  if (!isObj(v)) {
    e.add(path, 'must be an object');
    return null;
  }
  keys(v, ['id', 'name', 'type', 'parent', 'position', 'rotationDeg', 'material', 'params', 'hidden', 'locked'], path, e);
  const type = v['type'];
  if (typeof type !== 'string' || !Object.hasOwn(OBJECT_TYPES, type)) {
    e.add(`${path}.type`, `unsupported object type ${JSON.stringify(type)}`);
    return null;
  }
  const info = OBJECT_TYPES[type as ObjectType];
  const specs = info.params as Readonly<Record<string, ParamSpec>>;
  const params: Record<string, ParamValue> = {};
  const rawParams = v['params'] ?? {};
  if (!isObj(rawParams)) e.add(`${path}.params`, 'must be an object');
  else {
    keys(rawParams, Object.keys(specs), `${path}.params`, e);
    for (const [k, spec] of Object.entries(specs)) {
      if (rawParams[k] !== undefined) params[k] = paramValue(spec, rawParams[k], `${path}.params.${k}`, e);
    }
  }
  const out: TrackObject = {
    id: id(v['id'], `${path}.id`, e),
    name: str(v['name'], `${path}.name`, e, LIMITS.maxNameLength),
    type: type as ObjectType,
    position: vec3(v['position'], `${path}.position`, e, LIMITS.maxCoord),
    rotationDeg: v['rotationDeg'] === undefined ? [0, 0, 0] : vec3(v['rotationDeg'], `${path}.rotationDeg`, e, 3600),
    params,
  };
  if (v['parent'] !== undefined) out.parent = id(v['parent'], `${path}.parent`, e);
  if (info.material) out.material = str(v['material'], `${path}.material`, e, LIMITS.maxIdLength);
  else if (v['material'] !== undefined) e.add(`${path}.material`, `${type} objects have no material`);
  for (const flag of ['hidden', 'locked'] as const) {
    if (v[flag] === undefined) continue;
    if (typeof v[flag] !== 'boolean') e.add(`${path}.${flag}`, 'must be true or false');
    else if (v[flag]) out[flag] = true;
  }
  return out;
}

const PATTERNS: readonly SurfaceVisual['pattern'][] = ['marble', 'stone', 'brick', 'rune', 'sparkle', 'hazard', 'glyph', 'none'];
/** Allowed reactive behaviours and their parameter ranges: nothing else can reach the movement rules. */
const BEHAVIOR_RANGES: Readonly<Record<ReactiveBehavior['type'], Readonly<Record<string, [number, number]>>>> = {
  traction: { accelScale: [0, 4], momentumDecayScale: [0, 10], turnScale: [0, 4] },
  bend: { strength: [0, 2] },
  compression: { strength: [0, 2] },
  coupling: { strength: [0, 2] },
};
/** Solid materials must stay visible; lethal ones must also keep the hazard pattern. */
export const MIN_SOLID_OPACITY = 0.35;

function material(v: unknown, path: string, e: Errors): CustomMaterial | null {
  if (!isObj(v)) {
    e.add(path, 'must be an object');
    return null;
  }
  keys(v, ['id', 'name', 'kind', 'behaviors', 'visual'], path, e);
  const mid = str(v['id'], `${path}.id`, e, LIMITS.maxIdLength);
  if (!/^custom\.[a-z0-9-]{1,48}$/.test(mid)) e.add(`${path}.id`, 'must look like "custom.name" (lowercase letters, digits, "-")');
  const kind = v['kind'];
  if (kind !== 'safe' && kind !== 'lethal' && kind !== 'reactive') {
    e.add(`${path}.kind`, 'must be safe, lethal or reactive');
    return null;
  }
  const behaviors: ReactiveBehavior[] = [];
  const rawB = v['behaviors'] ?? [];
  if (!Array.isArray(rawB) || rawB.length > 4) e.add(`${path}.behaviors`, 'must be a list of at most 4 behaviours');
  else {
    rawB.forEach((b: unknown, i) => {
      const bp = `${path}.behaviors[${i}]`;
      if (!isObj(b) || typeof b['type'] !== 'string' || !Object.hasOwn(BEHAVIOR_RANGES, b['type'])) {
        e.add(bp, `type must be one of ${Object.keys(BEHAVIOR_RANGES).join(', ')}`);
        return;
      }
      const ranges = BEHAVIOR_RANGES[b['type'] as ReactiveBehavior['type']];
      keys(b, ['type', ...Object.keys(ranges)], bp, e);
      const out: Record<string, number | string> = { type: b['type'] };
      for (const [k, [lo, hi]] of Object.entries(ranges)) out[k] = num(b[k], `${bp}.${k}`, e, lo, hi);
      behaviors.push(out as unknown as ReactiveBehavior);
    });
  }
  if (kind !== 'reactive' && behaviors.length) e.add(`${path}.behaviors`, 'only reactive materials have behaviours');
  if (new Set(behaviors.map((b) => b.type)).size !== behaviors.length) e.add(`${path}.behaviors`, 'each behaviour type may appear once');
  const vis = v['visual'];
  let visual: SurfaceVisual = { color: 0x808080, emissive: 0, emissiveIntensity: 0, roughness: 0.8, pattern: 'stone', opacity: 1 };
  if (!isObj(vis)) e.add(`${path}.visual`, 'must be an object');
  else {
    keys(vis, ['color', 'emissive', 'emissiveIntensity', 'roughness', 'pattern', 'opacity'], `${path}.visual`, e);
    const colour = (k: string): number => {
      const c = vis[k];
      if (!Number.isInteger(c) || (c as number) < 0 || (c as number) > 0xffffff) {
        e.add(`${path}.visual.${k}`, 'must be an integer colour 0x000000–0xffffff');
        return 0;
      }
      return c as number;
    };
    const pattern = vis['pattern'];
    if (typeof pattern !== 'string' || !PATTERNS.includes(pattern as SurfaceVisual['pattern'])) e.add(`${path}.visual.pattern`, `must be one of ${PATTERNS.join(', ')}`);
    visual = {
      color: colour('color'),
      emissive: colour('emissive'),
      emissiveIntensity: num(vis['emissiveIntensity'], `${path}.visual.emissiveIntensity`, e, 0, 4),
      roughness: num(vis['roughness'], `${path}.visual.roughness`, e, 0, 1),
      pattern: (PATTERNS.includes(pattern as SurfaceVisual['pattern']) ? pattern : 'stone') as SurfaceVisual['pattern'],
      opacity: num(vis['opacity'], `${path}.visual.opacity`, e, MIN_SOLID_OPACITY, 1),
    };
    if (kind === 'lethal' && visual.pattern !== 'hazard') e.add(`${path}.visual.pattern`, 'lethal materials must use the "hazard" pattern so they stay recognisable');
  }
  return { id: mid, name: str(v['name'], `${path}.name`, e, LIMITS.maxNameLength), kind, behaviors, visual };
}

function editorMeta(v: unknown, e: Errors): EditorMeta {
  if (v === undefined) return {};
  if (!isObj(v)) {
    e.add('editor', 'must be an object');
    return {};
  }
  keys(v, ['camera', 'gridStep', 'angleStep', 'sketch', 'paint'], 'editor', e);
  const out: EditorMeta = {};
  if (v['camera'] !== undefined) {
    const c = v['camera'];
    if (!isObj(c)) e.add('editor.camera', 'must be an object');
    else {
      keys(c, ['position', 'target'], 'editor.camera', e);
      out.camera = { position: vec3(c['position'], 'editor.camera.position', e, LIMITS.maxCoord * 2), target: vec3(c['target'], 'editor.camera.target', e, LIMITS.maxCoord * 2) };
    }
  }
  if (v['gridStep'] !== undefined) out.gridStep = num(v['gridStep'], 'editor.gridStep', e, 0.01, 50);
  if (v['angleStep'] !== undefined) out.angleStep = num(v['angleStep'], 'editor.angleStep', e, 0.1, 90);
  if (v['sketch'] !== undefined) out.sketch = sketch(v['sketch'], e);
  if (v['paint'] !== undefined) {
    const p = paint(v['paint'], e);
    if (p) out.paint = p;
  }
  return out;
}

function paint(v: unknown, e: Errors): TrackPaint | null {
  const path = 'editor.paint';
  if (!isObj(v)) {
    e.add(path, 'must be an object');
    return null;
  }
  keys(v, ['cols', 'rows', 'cell', 'wallHeight', 'kinds', 'heights', 'allowAgent'], path, e);
  const size = (x: unknown, p: string): number => {
    const n = num(x, p, e, LIMITS.minPaintCells, LIMITS.maxPaintCells);
    if (!Number.isInteger(n)) e.add(p, 'must be an integer');
    return Math.min(LIMITS.maxPaintCells, Math.max(LIMITS.minPaintCells, Math.round(n)));
  };
  const cols = size(v['cols'], `${path}.cols`);
  const rows = size(v['rows'], `${path}.rows`);
  const cell = num(v['cell'], `${path}.cell`, e, 0.5, 8);
  const wallHeight = num(v['wallHeight'], `${path}.wallHeight`, e, 0.5, 30);
  const layer = (x: unknown, p: string, alphabet: string): string => {
    const r = decodeRuns(x as string, alphabet, cols * rows);
    if (typeof r === 'string') e.add(p, r);
    return typeof x === 'string' ? x : '';
  };
  const out: TrackPaint = { cols, rows, cell, wallHeight, kinds: layer(v['kinds'], `${path}.kinds`, PAINT_KIND_CODES), heights: layer(v['heights'], `${path}.heights`, PAINT_HEIGHT_CODES) };
  if (v['allowAgent'] !== undefined && typeof v['allowAgent'] !== 'boolean') e.add(`${path}.allowAgent`, 'must be true or false');
  return v['allowAgent'] === true ? { ...out, allowAgent: true } : out;
}

function sketch(v: unknown, e: Errors): TrackSketch {
  const path = 'editor.sketch';
  if (!isObj(v)) {
    e.add(path, 'must be an object');
    return { strokes: [], notes: [] };
  }
  keys(v, ['strokes', 'notes'], path, e);
  const list = (x: unknown, p: string, max: number): unknown[] => {
    if (!Array.isArray(x)) {
      e.add(p, 'must be an array');
      return [];
    }
    if (x.length > max) e.add(p, `at most ${max} entries`);
    return x.slice(0, max);
  };
  const seen = new Set<string>();
  const uniqueId = (x: unknown, p: string): string => {
    const s = id(x, p, e);
    if (seen.has(s)) e.add(p, `duplicate id "${s}"`);
    seen.add(s);
    return s;
  };
  const strokes: SketchStroke[] = list(v['strokes'], `${path}.strokes`, LIMITS.maxSketchStrokes).map((s, i) => {
    const p = `${path}.strokes[${i}]`;
    if (!isObj(s)) {
      e.add(p, 'must be an object');
      return { id: `s${i}`, points: [], width: 6 };
    }
    keys(s, ['id', 'points', 'width', 'built'], p, e);
    const points = list(s['points'], `${p}.points`, LIMITS.maxStrokePoints).map((q, j) => vec3(q, `${p}.points[${j}]`, e, LIMITS.maxCoord));
    if (points.length < 2) e.add(`${p}.points`, 'needs at least 2 points');
    if (s['built'] !== undefined && typeof s['built'] !== 'boolean') e.add(`${p}.built`, 'must be true or false');
    const stroke: SketchStroke = { id: uniqueId(s['id'], `${p}.id`), points, width: num(s['width'], `${p}.width`, e, 0.5, 60) };
    return s['built'] === true ? { ...stroke, built: true } : stroke;
  });
  const notes: SketchNote[] = list(v['notes'], `${path}.notes`, LIMITS.maxSketchNotes).map((n, i) => {
    const p = `${path}.notes[${i}]`;
    if (!isObj(n)) {
      e.add(p, 'must be an object');
      return { id: `n${i}`, position: [0, 0, 0], text: '' };
    }
    keys(n, ['id', 'position', 'text'], p, e);
    return { id: uniqueId(n['id'], `${p}.id`), position: vec3(n['position'], `${p}.position`, e, LIMITS.maxCoord), text: str(n['text'], `${p}.text`, e, LIMITS.maxNoteLength) };
  });
  return { strokes, notes };
}

/** Validates and normalises an untrusted document (already parsed JSON). */
export function parseTrackDocument(input: unknown): ParseResult {
  const e = new Errors();
  if (!isObj(input)) return { ok: false, errors: ['document: must be a JSON object'] };
  if (input['format'] !== TRACK_FORMAT) return { ok: false, errors: [`format: not a Xernous track (expected "${TRACK_FORMAT}")`] };
  const fv = input['formatVersion'];
  if (fv !== TRACK_FORMAT_VERSION) {
    const newer = isNum(fv) && fv > TRACK_FORMAT_VERSION;
    return { ok: false, errors: [`formatVersion: ${String(fv)} is not supported (this build reads version ${TRACK_FORMAT_VERSION}${newer ? '; the file is from a newer Xernous' : ''})`] };
  }
  keys(input, ['format', 'formatVersion', 'id', 'title', 'description', 'author', 'ruleset', 'killPlaneY', 'environment', 'materials', 'objects', 'editor'], 'document', e);
  const ruleset = input['ruleset'];
  if (!RULESETS.includes(ruleset as (typeof RULESETS)[number])) e.add('ruleset', `unsupported movement ruleset ${JSON.stringify(ruleset)} (supported: ${RULESETS.join(', ')})`);
  const env = input['environment'];
  const preset = isObj(env) && ENVIRONMENT_PRESETS.includes(env['preset'] as EnvironmentPreset) && Object.keys(env).length === 1 ? (env['preset'] as EnvironmentPreset) : null;
  if (!preset) e.add('environment', `must be { "preset": ${ENVIRONMENT_PRESETS.map((p) => JSON.stringify(p)).join(' | ')} }`);

  const rawMats = input['materials'] ?? [];
  const materials: CustomMaterial[] = [];
  if (!Array.isArray(rawMats)) e.add('materials', 'must be a list');
  else if (rawMats.length > LIMITS.maxCustomMaterials) e.add('materials', `at most ${LIMITS.maxCustomMaterials} custom materials`);
  else rawMats.forEach((m: unknown, i) => {
    const mat = material(m, `materials[${i}]`, e);
    if (mat) materials.push(mat);
  });

  const rawObjs = input['objects'];
  const objects: TrackObject[] = [];
  if (!Array.isArray(rawObjs)) e.add('objects', 'must be a list');
  else if (rawObjs.length > LIMITS.maxObjects) e.add('objects', `at most ${LIMITS.maxObjects} objects (found ${rawObjs.length})`);
  else
    for (let i = 0; i < rawObjs.length && !e.full; i++) {
      const o = object(rawObjs[i], `objects[${i}]`, e);
      if (o) objects.push(o);
    }

  const doc: TrackDocument = {
    format: TRACK_FORMAT,
    formatVersion: TRACK_FORMAT_VERSION,
    id: id(input['id'], 'id', e),
    title: str(input['title'], 'title', e, LIMITS.maxNameLength),
    description: str(input['description'], 'description', e, LIMITS.maxDescriptionLength),
    author: str(input['author'], 'author', e, LIMITS.maxNameLength),
    ruleset: 'xernous.baseline.phase3',
    killPlaneY: num(input['killPlaneY'], 'killPlaneY', e, -LIMITS.maxCoord, LIMITS.maxCoord),
    environment: { preset: preset ?? 'violet-dusk' },
    materials,
    objects,
    editor: editorMeta(input['editor'], e),
  };
  checkReferences(doc, e);
  return e.list.length ? { ok: false, errors: e.list } : { ok: true, doc: canonicalDoc(doc) };
}

/** Cross-object checks: unique ids, parents that exist and are groups, acyclic and shallow nesting, known materials. */
function checkReferences(doc: TrackDocument, e: Errors): void {
  const byId = new Map<string, TrackObject>();
  doc.objects.forEach((o, i) => {
    if (byId.has(o.id)) e.add(`objects[${i}].id`, `duplicate id "${o.id}"`);
    byId.set(o.id, o);
  });
  const matIds = new Set<string>(BUILTIN_MATERIALS.filter((m) => m.kind !== 'trigger').map((m) => m.id));
  doc.materials.forEach((m, i) => {
    if (matIds.has(m.id)) e.add(`materials[${i}].id`, `duplicate material id "${m.id}"`);
    matIds.add(m.id);
  });
  doc.objects.forEach((o, i) => {
    if (o.material !== undefined && !matIds.has(o.material)) e.add(`objects[${i}].material`, `unknown material "${o.material}"`);
    if (o.parent === undefined) return;
    const parent = byId.get(o.parent);
    if (!parent) return e.add(`objects[${i}].parent`, `no object "${o.parent}"`);
    if (parent.type !== 'group') return e.add(`objects[${i}].parent`, `"${o.parent}" is not a group`);
    let depth = 0;
    let cur: TrackObject | undefined = o;
    const seen = new Set<string>();
    while (cur?.parent !== undefined) {
      if (seen.has(cur.id)) return e.add(`objects[${i}].parent`, 'groups form a cycle');
      seen.add(cur.id);
      if (++depth > LIMITS.maxDepth) return e.add(`objects[${i}].parent`, `groups nest deeper than ${LIMITS.maxDepth}`);
      cur = byId.get(cur.parent);
    }
  });
}

/** Parses a JSON string with the size limit applied first. */
export function parseTrackJson(text: string): ParseResult {
  if (text.length > LIMITS.maxFileBytes) return { ok: false, errors: [`file: larger than ${(LIMITS.maxFileBytes / 1e6).toFixed(1)} MB`] };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err: unknown) {
    return { ok: false, errors: [`file: not valid JSON (${err instanceof Error ? err.message : String(err)})`] };
  }
  return parseTrackDocument(raw);
}

/** Rebuilds an object with a fixed key order (and spec order for params), so equal documents serialise identically. */
function canonicalObject(o: TrackObject): TrackObject {
  const specs = OBJECT_TYPES[o.type].params as Readonly<Record<string, ParamSpec>>;
  const params: Record<string, ParamValue> = {};
  for (const k of Object.keys(specs)) if (o.params[k] !== undefined) params[k] = o.params[k];
  return {
    id: o.id,
    name: o.name,
    type: o.type,
    ...(o.parent !== undefined ? { parent: o.parent } : {}),
    position: o.position,
    rotationDeg: o.rotationDeg,
    ...(o.material !== undefined ? { material: o.material } : {}),
    params,
    ...(o.hidden ? { hidden: true } : {}),
    ...(o.locked ? { locked: true } : {}),
  };
}

function canonicalDoc(doc: TrackDocument): TrackDocument {
  return {
    format: doc.format,
    formatVersion: doc.formatVersion,
    id: doc.id,
    title: doc.title,
    description: doc.description,
    author: doc.author,
    ruleset: doc.ruleset,
    killPlaneY: doc.killPlaneY,
    environment: { preset: doc.environment.preset },
    materials: doc.materials.map((m) => ({ id: m.id, name: m.name, kind: m.kind, behaviors: m.behaviors, visual: m.visual })),
    objects: doc.objects.map(canonicalObject),
    editor: doc.editor,
  };
}

/** Canonical serialisation for export and storage. */
export function serializeTrack(doc: TrackDocument): string {
  return JSON.stringify(canonicalDoc(doc), null, 2);
}
